import { Button, Checkbox, ListItemText, MenuItem, Select } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePicker, message } from "antd";
import dayjs from "dayjs";
import moment from "moment";

import HeaderBread from "../../../../../components/HeaderBread";
import { useDataLayerValue } from "../../../../../DataLayer/DataLayer";
import Layout from "../../../../../Layout";
import CustomHeaderTitle from "../../../../../components/constants/CustomHeaderTitle";
import CustomizedTable from "../../../../../components/UI/CustomizedTable";
import ReportDownloads from "../../../../../components/ReportDownloads";
import SwitchBar from "../../../../../components/Pages/SwitchBar";
import {
  getShorexSummaryReport,
  getShorexBookingPackageOptions,
} from "../../../../../utils/ApiCalls/BackOffice/Shorex";
import { createReportDownload } from "../../../../../utils/ApiCalls/ReportDownloads";
import { addlog } from "../../../../../utils/ApiCalls/Logs";
import { LOG_ACTIONS, LOG_TYPES } from "../../../../../utils/Constant/LogConstant";
import { getReportFileName } from "../../../../../utils/Constant/ReportDownloadLabels";
import { checkPermission } from "../../../../../utils/Validation/checkPermission";

const { RangePicker } = DatePicker;
const PAGE_PATH = "/shore/back-office/shore-excursions/report/summary";

// excNo/name alone can repeat across different sailing dates (different
// excId) - the date disambiguates them. Kept separate from the base label so
// list rows can show it as a secondary line instead of crowding one line.
const excursionOptionDate = (exc) =>
  exc.startDate ? moment.utc(exc.startDate).format("YYYY-MM-DD") : undefined;
const excursionOptionLabel = (exc) => {
  const date = excursionOptionDate(exc);
  return `${exc.excNo}-${exc.name}${date ? ` (${date})` : ""}`;
};

const getDefaultDateRange = () =>
  JSON.stringify([moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")]);

const parseDateRange = (range) => {
  if (!range) return null;
  try {
    const [start, end] = JSON.parse(range);
    if (start && end) return [dayjs(start, "YYYY-MM-DD"), dayjs(end, "YYYY-MM-DD")];
  } catch (e) {
    // unparsable - treat as no selection
  }
  return null;
};

const formatAmount = (val) => (val != null ? Number(val).toFixed(2) : "-");

const ShorexSummaryReport = () => {
  const locationP = useLocation();
  const navigate = useNavigate();
  const [{ permission, userDetails }, dispatch] = useDataLayerValue();
  const [topTab, setTopTab] = useState("report");

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Report", route: "/shore/back-office/shore-excursions/report" },
    { title: "Summary Report", route: PAGE_PATH },
  ];

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [dateRange, setDateRange] = useState(() => {
    const params = new URLSearchParams(locationP.search);
    return params.has("dateRange") ? params.get("dateRange") : getDefaultDateRange();
  });
  const [page, setPage] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("page")) || 1
  );
  const [pageSize, setPageSize] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("pageSize")) || 10
  );
  const parseIdList = (val) => (val ? val.split(",").filter(Boolean) : []);
  const [excIds, setExcIds] = useState(
    () => parseIdList(new URLSearchParams(locationP.search).get("excIds"))
  );
  const [excOptions, setExcOptions] = useState([]);

  useEffect(() => {
    (async () => {
      const excRes = await getShorexBookingPackageOptions();
      const sortAlphaNumeric = (a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
      setExcOptions(
        (excRes?.data || [])
          .slice()
          .sort((a, b) => sortAlphaNumeric(excursionOptionLabel(a), excursionOptionLabel(b)))
      );
    })();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("page", page);
    params.set("pageSize", pageSize);
    if (excIds.length) params.set("excIds", excIds.join(","));
    params.set("dateRange", dateRange);
    navigate({ search: params.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, dateRange, excIds]);

  const columns = [
    {
      title: "Sl",
      key: "sl",
      width: 40,
      render: (val, rec, idx) => (page - 1) * pageSize + idx + 1,
    },
    {
      title: "Fidelio Business Date",
      dataIndex: "fidelioBusinessDate",
      key: "fidelioBusinessDate",
      width: 150,
      render: (val) => (val ? moment(val).format("YYYY-MM-DD") : "-"),
    },
    {
      title: "Excursion Date",
      dataIndex: "excDate",
      key: "excDate",
      width: 130,
      // excDate is a calendar date, not an instant - .utc() reads its date
      // part directly instead of letting the browser's local tz shift it.
      render: (val) => (val ? moment.utc(val).format("YYYY-MM-DD") : "-"),
    },
    { title: "Excursion Code", dataIndex: "excNumber", key: "excNumber", width: 120 },
    { title: "Excursion Name", dataIndex: "excName", key: "excName", width: 160 },
    { title: "Transaction Count", dataIndex: "transactionCount", key: "transactionCount", width: 130 },
    { title: "Adult Pax", dataIndex: "adultPax", key: "adultPax", width: 90 },
    { title: "Child Pax", dataIndex: "childPax", key: "childPax", width: 90 },
    { title: "Infant Pax", dataIndex: "infantPax", key: "infantPax", width: 90 },
    { title: "Sold Quantity", dataIndex: "soldQuantity", key: "soldQuantity", width: 120 },
    { title: "Voided Quantity", dataIndex: "voidedQuantity", key: "voidedQuantity", width: 130 },
    { title: "Net Quantity", dataIndex: "netQuantity", key: "netQuantity", width: 120 },
    {
      title: "Gross Sales",
      dataIndex: "grossSales",
      key: "grossSales",
      width: 110,
      render: (val) => formatAmount(val),
    },
    { title: "Voided Transaction", dataIndex: "voidedTransaction", key: "voidedTransaction", width: 140 },
    {
      title: "Voided Amount",
      dataIndex: "voidedAmount",
      key: "voidedAmount",
      width: 130,
      render: (val) => formatAmount(val),
    },
    {
      title: "Net Sales",
      dataIndex: "netSales",
      key: "netSales",
      width: 110,
      render: (val) => formatAmount(val),
    },
  ];

  const buildFilterArgs = () => {
    const args = {};
    args.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (excIds.length) args.excIds = excIds.join(",");
    try {
      const [fromDate, toDate] = JSON.parse(dateRange || "[]");
      if (fromDate && toDate) {
        args.fromDate = fromDate;
        args.toDate = toDate;
      }
    } catch (e) {
      // no date range selected
    }
    return args;
  };

  const getData = async (value) => {
    dispatch({ type: "SET_LOADING", loading: true });
    const args = { page: value || page, limit: pageSize, ...buildFilterArgs() };
    let data = await getShorexSummaryReport(args);
    if (data.error) {
      dispatch({ type: "SET_ERROR_MESSAGE", errMess: { message: data.error, type: "error" } });
    } else {
      setRows(data.data || []);
      setCount(data.totalCount || 0);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  const handleRequestDownload = async () => {
    if (!checkPermission(permission, "download_per", "Summary Report", window?.location?.pathname)) {
      message.error("Download permission missing");
      return;
    }
    const res = await createReportDownload({
      moduleName: "shorex_summary_report",
      createdBy: userDetails.id,
      searchParams: buildFilterArgs(),
    });
    if (res?.error) {
      dispatch({ type: "SET_ERROR_MESSAGE", errMess: { message: res.error, type: "error" } });
    } else {
      addlog(
        LOG_ACTIONS.REQUESTED,
        LOG_TYPES.REPORT_DOWNLOADS,
        {
          title: "Shorex Summary Report download requested",
          moduleName: "shorex_summary_report",
          reportDownloadId: res?.id,
          fileName: getReportFileName("shorex_summary_report", res?.createdAt, res?.id, res?.timezone),
        },
        userDetails.id
      );
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: "Download requested - check Download Logs tab", type: "success" },
      });
    }
  };

  useEffect(() => {
    getData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, dateRange, excIds]);

  return (
    <Layout type={"read_per"} module_data={"Summary Report"}>
      <CustomHeaderTitle title={"Shore Excursion Summary Report"} />
      <div>
        <HeaderBread navigation={navigation} title={"Summary Report"} />
      </div>
      <section className="content" style={{ marginTop: "20px" }}>
        <div className="container-fluid">
          <SwitchBar
            tabs={[
              { title: "Summary Report", value: "report" },
              { title: "Download Logs", value: "downloads" },
            ]}
            activeTab={topTab}
            setActiveTab={setTopTab}
          />

          {topTab === "downloads" ? (
            <ReportDownloads module="shorex_summary_report" />
          ) : (
          <div className="card">
            <div className="card-header two_item" style={{ alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <Select
                  multiple
                  style={{ width: "220px", height: "40px" }}
                  displayEmpty
                  value={excIds}
                  onChange={(e) => {
                    setExcIds(e.target.value);
                    setPage(1);
                  }}
                  renderValue={(selected) =>
                    selected.length
                      ? excOptions
                          .filter((exc) => selected.includes(String(exc.excId)))
                          .map((exc) => excursionOptionLabel(exc))
                          .join(", ")
                      : "Filter by Excursion"
                  }
                >
                  {excOptions.map((exc) => (
                    <MenuItem key={exc.excId} value={String(exc.excId)}>
                      <Checkbox checked={excIds.includes(String(exc.excId))} />
                      <ListItemText
                        primary={`${exc.excNo}-${exc.name}`}
                        secondary={excursionOptionDate(exc)}
                      />
                    </MenuItem>
                  ))}
                </Select>
                <RangePicker
                  style={{ width: "250px" }}
                  onChange={(date, dateString) => {
                    setDateRange(dateString[0] && dateString[1] ? JSON.stringify(dateString) : "");
                    setPage(1);
                  }}
                  value={parseDateRange(dateRange)}
                  format={"YYYY-MM-DD"}
                />
                <Button
                  variant="contained"
                  style={{ background: "var(--red)", fontWeight: "500" }}
                  onClick={() => {
                    setDateRange(getDefaultDateRange());
                    setExcIds([]);
                    setPage(1);
                  }}
                >
                  Reset Filter
                </Button>
                <Button
                  variant="contained"
                  style={{ background: "#1565c0", fontWeight: "500" }}
                  onClick={handleRequestDownload}
                >
                  Request Download
                </Button>
              </div>
            </div>
            <div className="card-body">
              <div className="table_holder">
                <CustomizedTable
                  setRows={setRows}
                  setPageSize={setPageSize}
                  setPage={setPage}
                  pageSize={pageSize}
                  page={page}
                  count={count}
                  columns={columns}
                  rows={rows}
                  title={"Summary Report"}
                />
              </div>
            </div>
          </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default ShorexSummaryReport;
